import { useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { apiService } from "../services/api";
import { useToast } from "../hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { MessageStats } from "../components/message/MessageStats";
import { MessageList } from "../components/message/MessageList";
import { Message, SuccessResponse } from "../types/generated";

const Dashboard = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const limit = 20;
  const [confirmSend, setConfirmSend] = useState<number | null>(null);

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: ["dashboard-messages"],
    queryFn: async ({ pageParam = 1 }) => {
      return apiService.getMessages(pageParam, limit);
    },
    getNextPageParam: (lastPage: SuccessResponse<Message[]>, allPages) => {
      const total = Number(lastPage.data.length);
      const totalPages = Math.ceil(total / limit);
      const nextPage = allPages.length + 1;
      return nextPage <= totalPages ? nextPage : undefined;
    },
    initialPageParam: 1,
  });

  const messages = data?.pages.flatMap((page) => page.data) || [];

  const handleSendMessage = async (messageId: number) => {
    try {
      await apiService.sendMessage(messageId);

      toast({
        title: t("common.success"),
        description: t("message.sent_successfully"),
      });

      refetch();
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description:
          t("message.error_sending_message") + " " + t("common.try_again"),
      });
    } finally {
      setConfirmSend(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <div className="text-center text-red-500">
          <p>{t("message.error_loading_messages")}</p>
        </div>
      </div>
    );
  }

  const next24Hours = new Date();
  next24Hours.setHours(next24Hours.getHours() + 24);

  const upcomingMessages =
    messages.filter((m) => {
      if (m.status !== "scheduled") return false;
      const scheduleDate = m.scheduled ? new Date(m.scheduled) : null;
      return scheduleDate && scheduleDate <= next24Hours;
    }) || [];

  // Sort by scheduled date
  upcomingMessages.sort((a, b) => {
    const dateA = a.scheduled ? new Date(a.scheduled) : new Date();
    const dateB = b.scheduled ? new Date(b.scheduled) : new Date();
    return dateA.getTime() - dateB.getTime();
  });

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl relative">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
        <h1 className="text-3xl font-bold">{t("pages.dashboard.title")}</h1>
        <p className="text-muted-foreground">{t("pages.dashboard.subtitle")}</p>
      </div>

      <MessageStats messages={messages || []} />

      <MessageList
        title={t("pages.dashboard.upcoming_messages")}
        messages={upcomingMessages}
        hasMore={hasNextPage}
        isLoadingMore={isFetchingNextPage}
        onLoadMore={() => fetchNextPage()}
      />

      <Dialog
        open={confirmSend !== null}
        onOpenChange={() => setConfirmSend(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("pages.dashboard.confirm_send_title")}</DialogTitle>
            <DialogDescription>
              {t("pages.dashboard.confirm_send_description")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmSend(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              onClick={() => confirmSend && handleSendMessage(confirmSend)}
            >
              {t("common.send")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Button
        onClick={() => navigate("/messages/create")}
        size="icon"
        className="fixed bottom-6 left-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl"
      >
        <Plus className="h-6 w-6" />
      </Button>
    </div>
  );
};

export default Dashboard;
